/**
 * Metadata API — exact mirror of RW-ERP-Service `getAttributeMetaData()`
 * in `src/api/metaData/meta-data.api.ts:20-56`.
 *
 *   POST {API_BASE_URL}/v1/users/metadata/domain/search
 *   body: composite filter with `domainId AMONG REACT_DOMAIN_IDS`
 *
 * Returns the raw response body. Unwrapping + caching live in
 * `src/stores/metadata-store.ts` so the layering matches Vue's
 * `api → store → page` flow.
 *
 * The request body is built via the shared DSL in `./filters/compositeFilter.ts`
 * so every server-side filter envelope in the app has a single canonical
 * shape and operator vocabulary.
 */
import apiClient from "../config/axios.config";
import { REACT_DOMAIN_IDS } from "../constants/domainIds";
import type { MetadataResponse } from "../types/metadata";
import { CompositeFilter, among, and } from "./filters/compositeFilter";

const METADATA_ENDPOINT = "/api/v1/users/metadata/domain/search";

export interface MetadataApiBody {
  data?: MetadataResponse;
}

/**
 * Bulk-fetches attribute metadata for every domain ID the React app
 * cares about (defaults to `REACT_DOMAIN_IDS`). Returns the raw response
 * body — store layer is responsible for `body.data` unwrap + cache.
 */
export async function getAttributeMetaData(
  domainIds: ReadonlyArray<string> = REACT_DOMAIN_IDS,
): Promise<MetadataApiBody> {
  const requestBody: CompositeFilter = and(among("domainId", domainIds));

  const response = await apiClient.post<MetadataApiBody>(
    METADATA_ENDPOINT,
    requestBody,
  );
  return response.data;
}
